import {
  Create,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useTranslate,
  useCreate,
  AutocompleteArrayInput,
  SelectInput,
  Toolbar
} from "react-admin";
import * as Sentry from "@sentry/react";
import { useNavigate, useLocation } from "react-router-dom";
import { RRule } from "rrule";
import * as User from "../providers/user";
import CustomSaveButton from "./CustomSaveButton";
import { useState } from "react";
import { GET_ARTISTS, GET_LOCATIONS } from "../gql";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import TimePickerInput from "./TimePickerInput";
import DatePickerInput from "./DatePickerInput";

interface AvailabilityCreateProps {
  selectedDate?: string;
  selectedStart?: string;
  selectedEnd?: string;
  closeCreateDialogAndRefresh?: () => void;
  setLoading: (loading: boolean) => void;
  loading: boolean;
}

interface LocationResult {
  id: string;
  company_id: string;
  timezone: string;
}

export default function AvailabilityCreate({
  selectedStart,
  selectedEnd,
  closeCreateDialogAndRefresh,
  setLoading,
  loading
}: AvailabilityCreateProps) {
  const { user, permissions } = User.useUser();
  const translate = useTranslate();
  const [create] = useCreate();
  const navigate = useNavigate();
  const location = useLocation();
  const [recurringChoice, setRecurringChoice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const artistsResult = useQuery(GET_ARTISTS, {
    variables: {
      where: {
        user: {
          is: {
            joined_company_id: { equals: permissions?.companyId }
          }
        },
        is_active: {
          equals: true
        }
      }
    },
    fetchPolicy: "no-cache"
  });
  const data = artistsResult?.data ?? artistsResult?.previousData;

  const locationsResult = useQuery(GET_LOCATIONS, {
    variables: {
      where: {
        company_id: { equals: permissions?.companyId }
      }
    },
    fetchPolicy: "no-cache"
  });
  const locationsData: LocationResult[] =
    (locationsResult?.data ?? locationsResult?.previousData)?.locations ?? [];

  const submit = (data: any) => {
    if (loading) return;
    setLoading(true);

    const artists = data[
      "@@ra-many-to-many/Availability/ArtistAvailability/Artist"
    ]?.map((artistId: string) => ({
      artist: { id: artistId }
    }));

    const locationTimezone =
      locationsData.find((location) => location.id === data.location.id)
        ?.timezone ?? "PST8PDT";

    // Parse the date in the target time zone
    const mdate = dayjs.tz(data.date as string, locationTimezone);

    // Prepare date and time components
    const padZero = (num: any) => num.toString().padStart(2, "0");

    const year = mdate.year();
    const month = padZero(mdate.month() + 1);
    const day = padZero(dayjs(data.date as string).date());
    const startHour = padZero(data.start.getHours());
    const startMinute = padZero(data.start.getMinutes());
    const endHour = padZero(
      data.end.getHours() === 0 ? 24 : data.end.getHours()
    );
    const endMinute = padZero(data.end.getMinutes());

    const startDateTimeString = `${year}-${month}-${day} ${startHour}:${startMinute}`;
    const endDateTimeString = `${year}-${month}-${day} ${endHour}:${endMinute}`;

    // Parse start and end times in the target time zone
    const start_time = dayjs.tz(
      startDateTimeString,
      "YYYY-MM-DD HH:mm",
      locationTimezone
    );
    const end_time = dayjs.tz(
      endDateTimeString,
      "YYYY-MM-DD HH:mm",
      locationTimezone
    );

    const retval: any = {
      inserted_by: {
        id: user?.impersonate_id || user?.id
      },
      location: {
        id: String(data.location.id)
      },
      start_time: start_time.format(),
      end_time: end_time.format(),
      status: "confirmed",
      artists
    };

    if (data.recurrence) {
      const ruleOptions: any = {
        freq: data.recurrence,
        dtstart: start_time.toDate()
      };

      if (data.end_recurrence) {
        // User has specified an end recurrence date
        const recurrenceEndDate = dayjs
          .tz(data.end_recurrence as string, "YYYY-MM-DD", locationTimezone)
          .endOf("day")
          .toDate(); // Include the entire end date
        ruleOptions.until = recurrenceEndDate;
      } else {
        // No end recurrence date specified, set a default count
        let count;
        if (data.recurrence === RRule.DAILY) {
          count = 365; // Default to one year
        } else if (data.recurrence === RRule.WEEKLY) {
          count = 52; // Default to one year
        } else if (data.recurrence === RRule.MONTHLY) {
          count = 12; // Default to one year
        } else {
          count = 12; // Default count for other frequencies
        }
        ruleOptions.count = count;
      }

      const rule = new RRule(ruleOptions);

      const occurrenceDates = rule.all();

      const duration = end_time.diff(start_time);

      const desiredHour = start_time.hour();
      const desiredMinute = start_time.minute();
      retval.recurring = true;

      const availabilities = occurrenceDates.map((occurrenceDate) => {
        // Format the occurrence date
        const occurrenceDateString = dayjs(occurrenceDate).format("YYYY-MM-DD");

        // Set the occurrence time in the target time zone with the desired local time
        const occurrenceStart = dayjs.tz(
          `${occurrenceDateString} ${padZero(desiredHour)}:${padZero(
            desiredMinute
          )}`,
          "YYYY-MM-DD HH:mm",
          locationTimezone
        );

        const occurrenceEnd = occurrenceStart.add(duration, "milliseconds");

        return {
          ...retval,
          start_time: occurrenceStart.format(),
          end_time: occurrenceEnd.format()
        };
      });

      create(
        "GroupAvailability",
        {
          data: { availabilities }
        },
        {
          onSuccess: () =>
            closeCreateDialogAndRefresh
              ? closeCreateDialogAndRefresh()
              : navigate("/dashboard/Availability"),
          onSettled: () => setLoading(false),
          onError: (error: any) => {
            setError(error.message);
            setLoading(false);
          }
        }
      );
    } else {
      create(
        "Availability",
        {
          data: retval
        },
        {
          onSuccess: closeCreateDialogAndRefresh
            ? () => closeCreateDialogAndRefresh()
            : () => navigate("/dashboard/Availability"),
          onSettled: () => setLoading(false),
          onError: (error: any) => {
            Sentry.captureException(error);
            setError(error.message);
            setLoading(false);
          }
        }
      );
    }
  };

  const getTimeOfDayInMilliseconds = (date: Date) => {
    const hours = date.getHours() * 60 * 60 * 1000;
    const minutes = date.getMinutes() * 60 * 1000;
    return hours + minutes;
  };

  const validateDates = (values: any) => {
    const errors: any = {};
    const artists =
      values["@@ra-many-to-many/Availability/ArtistAvailability/Artist"];
    if (!values?.date) {
      errors.date = translate("ra.validation.required");
    }
    if (!values?.start) {
      errors.start = translate("ra.validation.required");
    }
    if (!values?.end) {
      errors.end = translate("ra.validation.required");
    }
    if (values?.start && values?.end) {
      const start = getTimeOfDayInMilliseconds(values.start);
      const end = getTimeOfDayInMilliseconds(values.end);
      if (start >= end) {
        errors.end = "End date must be after start date";
      }
    }
    // if (start && end && start >= end) {
    //   errors.end = "End date must be after start date";
    // }
    if (!artists?.length) {
      errors["@@ra-many-to-many/Availability/ArtistAvailability/Artist"] =
        "At least one artist is required";
    }
    if (!values?.location?.id) {
      errors["location.id"] = "Location is required";
    }
    return errors;
  };

  const disabled =
    permissions.role.role !== "OWNER" &&
    permissions.role.role !== "MANAGER" &&
    !permissions.isSuperAdmin;

  return (
    <Create
      redirect="list"
      sx={{
        backgroundColor: "#fff",
        "& .RaToolbar-desktopToolbar": {
          backgroundColor: "#fff",
          minHeight: "40px",
          padding: "0px 16px 16px 16px"
        },
        "& .RaCreate-noActions": {
          marginTop: 0
        },
        "& .MuiFilledInput-root": {
          borderRadius: "8px",
          border: "1px solid rgba(48, 47, 46, 0.06)"
        },
        "& .MuiFilledInput-input": {
          height: "24px",
          fontSize: "16px",
          borderRadius: "8px",
          fontFamily: "Inter",
          border: "none"
        },
        "& .MuiFilledInput-input:focus": {
          boxShadow: "none",
          borderRadius: "8px !important"
        },
        "& .MuiFilledInput-root:before": {
          border: "none !important"
        },
        "& .MuiFilledInput-root:after": {
          border: "1px solid #b8722a !important",
          top: "0px",
          borderRadius: "8px",
          transition: "none"
        },
        "& .MuiFilledInput-root.Mui-error:after": {
          border: "1px solid #EF5B1B !important"
        },
        "& .MuiFilledInput-root.Mui-disabled": {
          backgroundColor: "rgba(48, 47, 46, 0.06)"
        },
        "& .MuiTextField-root": {
          marginTop: "3px",
          marginBottom: "3px"
        },
        "& .MuiInputLabel-root": {
          fontSize: "16px",
          color: "rgba(48, 47, 46, 0.6)"
        },
        "& .MuiInputLabel-root.Mui-focused": {
          color: "#b8722a"
        },
        "& .MuiFormHelperText-root": {
          fontSize: "12px",
          marginTop: "2px",
          marginBottom: "2px",
          lineHeight: "14px"
        }
      }}
    >
      <SimpleForm
        onSubmit={submit}
        validate={validateDates}
        sx={{ backgroundColor: "#fff", padding: "0px 16px !important" }}
        toolbar={
          <>
            {error && <p style={{ color: "red" }}>{error}</p>}
            <CustomCreateToolbar loading={loading} disabled={disabled} />
          </>
        }
      >
        <DatePickerInput
          source="date"
          disabled={disabled}
          label={translate("availability.date")}
          defaultValue={
            selectedStart
              ? new Date(selectedStart)
              : location.state?.range?.start_time
          }
          validate={[required()]}
          fullWidth
        />
        <TimePickerInput
          source="start"
          disabled={disabled}
          label={translate("availability.start")}
          defaultValue={
            selectedStart
              ? new Date(selectedStart)
              : location.state?.range?.start_time
          }
          validate={[required()]}
          // color="primary"
          fullWidth
        />
        <TimePickerInput
          source="end"
          disabled={disabled}
          label={translate("availability.end")}
          defaultValue={
            selectedEnd
              ? new Date(selectedEnd)
              : location.state?.range?.end_time
          }
          validate={[required()]}
          // inputProps={{ color: "secondary", slotProps: { color: "secondary" } }}
          // SelectProps={{ color: "secondary" }}
          // color="primary"
          fullWidth
        />
        <SelectInput
          source="recurrence"
          label={translate("availability.repeat")}
          choices={[
            { id: RRule.DAILY, name: "Daily" },
            { id: RRule.WEEKLY, name: "Weekly" }
          ]}
          onChange={(e) => setRecurringChoice(e.target.value)}
          fullWidth
        />
        {recurringChoice && (
          <DatePickerInput
            disabled={disabled}
            source="end_recurrence"
            defaultValue={
              selectedStart
                ? new Date(selectedStart).getTime() + 365 * 24 * 60 * 60 * 1000
                : location.state?.range?.start_time
            }
            label={translate("availability.end_recurrence")}
            validate={[required()]}
            fullWidth
          />
        )}
        {locationsData && locationsData.length > 0 && (
          <ReferenceInput
            source="location.id"
            reference="Location"
            filter={{ company_id: permissions.companyId }}
          >
            <AutocompleteInput
              optionText={(value) => value.nickname || value.name}
              disabled={disabled}
              label={translate("availability.location")}
              fullWidth
              validate={[required()]}
            />
          </ReferenceInput>
        )}
        {data?.artists && data.artists.length > 0 && (
          <AutocompleteArrayInput
            source="@@ra-many-to-many/Availability/ArtistAvailability/Artist"
            choices={data.artists}
            disabled={disabled}
            optionText={(value) =>
              `${value.user.first_name} ${value.user.last_name}`
            }
            label={translate("pagenames.artists")}
            fullWidth
            validate={[required()]}
            matchSuggestion={(_value, choices) =>
              choices?.user?.joined_company_id === permissions.companyId
            }
            setFilter={() => {}}
          />
        )}
      </SimpleForm>
    </Create>
  );
}

const CustomCreateToolbar = ({
  loading,
  disabled
}: {
  loading: boolean;
  disabled: boolean;
}) => (
  <Toolbar
    sx={{
      justifyContent: "flex-end",
      "& .MuiButton-containedPrimary": {
        borderRadius: "32px",
        backgroundColor: "rgba(34, 46, 66, 1)",
        fontSize: "14px",
        textTransform: "capitalize",
        boxShadow: "none",
        "&:hover": {
          backgroundColor: "rgba(22, 30, 44, 1)"
        }
      },
      "& .MuiButtonBase-root.Mui-disabled": {
        backgroundColor: "rgba(48, 47, 46, 0.12)"
      }
    }}
  >
    <CustomSaveButton isLoading={loading} disabled={loading || disabled} />
  </Toolbar>
);
